import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_thriveit-preview/artifacts/xidnz9yy_PNG-02%20%282%29.png';
const CALENDLY_URL = 'https://calendly.com/satyam-thriveitmedia/discovery-call';

const services = [
  { name: 'Influencer Marketing', path: '/services/influencer-marketing' },
  { name: 'AI UGC Ads', path: '/services/ai-ugc-ads' },
  { name: 'Real UGC', path: '/services/real-ugc' },
  { name: 'Social Media Content', path: '/services/social-media-content' },
];

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;
  const isServiceActive = location.pathname.startsWith('/services');

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex-shrink-0">
            <img src={LOGO_URL} alt="Thriveit Media Logo" className="h-12 w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-10">
            <Link
              to="/"
              data-testid="nav-home"
              className={`text-sm font-body font-normal tracking-wide transition-colors duration-300 ${
                isActive('/') ? 'text-[#FF5C35]' : 'text-[#F5F5F0] hover:text-[#FF5C35]'
              }`}
            >
              Home
            </Link>

            <div className="relative group">
              <button
                data-testid="nav-services-dropdown"
                className={`flex items-center gap-1 text-sm font-body font-normal tracking-wide transition-colors duration-300 ${
                  isServiceActive ? 'text-[#FF5C35]' : 'text-[#F5F5F0] hover:text-[#FF5C35]'
                }`}
                onClick={() => setServicesOpen(!servicesOpen)}
              >
                Services <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
              </button>
              <div className="absolute top-full left-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                <div className="bg-[#111111] border border-white/5 p-2 min-w-[240px]">
                  <Link
                    to="/services"
                    data-testid="nav-all-services"
                    className="block px-4 py-2.5 text-sm text-[#888880] hover:text-[#FF5C35] hover:bg-white/5 transition-colors"
                  >
                    All Services
                  </Link>
                  {services.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      data-testid={`nav-${s.path.split('/').pop()}`}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        isActive(s.path) ? 'text-[#FF5C35]' : 'text-[#F5F5F0] hover:text-[#FF5C35] hover:bg-white/5'
                      }`}
                    >
                      {s.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/about"
              data-testid="nav-about"
              className={`text-sm font-body font-normal tracking-wide transition-colors duration-300 ${
                isActive('/about') ? 'text-[#FF5C35]' : 'text-[#F5F5F0] hover:text-[#FF5C35]'
              }`}
            >
              About
            </Link>
            <Link
              to="/blog"
              data-testid="nav-blog"
              className={`text-sm font-body font-normal tracking-wide transition-colors duration-300 ${
                isActive('/blog') ? 'text-[#FF5C35]' : 'text-[#F5F5F0] hover:text-[#FF5C35]'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact"
              className={`text-sm font-body font-normal tracking-wide transition-colors duration-300 ${
                isActive('/contact') ? 'text-[#FF5C35]' : 'text-[#F5F5F0] hover:text-[#FF5C35]'
              }`}
            >
              Contact
            </Link>
          </div>

          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-book-call"
            className="hidden lg:inline-flex items-center px-6 py-2.5 bg-[#FFE500] text-[#0a0a0a] text-sm font-body font-medium tracking-wide hover:bg-[#FF5C35] transition-colors duration-300"
          >
            Book a Free Call
          </a>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#F5F5F0] p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0a0a0a]/98 backdrop-blur-xl border-t border-white/5">
          <div className="px-6 py-6 space-y-1">
            <Link to="/" data-testid="mobile-nav-home" className="block py-3 text-[#F5F5F0] text-lg font-body">Home</Link>
            <div>
              <button
                data-testid="mobile-services-toggle"
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center justify-between w-full py-3 text-[#F5F5F0] text-lg font-body"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 space-y-1 pb-2">
                  <Link to="/services" data-testid="mobile-nav-all-services" className="block py-2 text-[#888880] text-base">All Services</Link>
                  {services.map((s) => (
                    <Link key={s.path} to={s.path} data-testid={`mobile-nav-${s.path.split('/').pop()}`} className="block py-2 text-[#888880] hover:text-[#FF5C35] text-base">
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/about" data-testid="mobile-nav-about" className="block py-3 text-[#F5F5F0] text-lg font-body">About</Link>
            <Link to="/blog" data-testid="mobile-nav-blog" className="block py-3 text-[#F5F5F0] text-lg font-body">Blog</Link>
            <Link to="/contact" data-testid="mobile-nav-contact" className="block py-3 text-[#F5F5F0] text-lg font-body">Contact</Link>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-cta"
              className="block w-full text-center mt-4 px-6 py-3 bg-[#FFE500] text-[#0a0a0a] font-medium text-base"
            >
              Book a Free Call
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
